import { useEffect, useState } from "react";
import api from "../lib/api";

// Cache affiliate settings (frequency cap + one-per-page) with a short TTL.
let _settings = { frequency_cap: 0, one_per_page: true };
let _fetchedAt = 0;
const TTL_MS = 15000;
function getSettings() {
  const now = Date.now();
  if (now - _fetchedAt < TTL_MS) return Promise.resolve(_settings);
  return api.get("/affiliate/settings")
    .then((r) => { _settings = { frequency_cap: r.data.frequency_cap || 0, one_per_page: r.data.one_per_page !== false }; _fetchedAt = now; return _settings; })
    .catch(() => _settings);
}
function seenMap() { try { return JSON.parse(sessionStorage.getItem("pti_aff_seen") || "{}"); } catch { return {}; } }
function bumpSeen(id) { const m = seenMap(); m[id] = (m[id] || 0) + 1; sessionStorage.setItem("pti_aff_seen", JSON.stringify(m)); }

// Module-level guard so a single logical mount fires exactly once (survives StrictMode double-invoke).
const _fired = new Set();
// Per page load: which single affiliate id "won" the page when one-per-page is on.
let _pageWinner = null;
let _pageLoadKey = typeof window !== "undefined" ? window.location.href : "";

// Renders an admin-managed affiliate creative inside a clean white PictureThisInk ad card.
// Supports either a plain image (ad.image) or sanitized partner HTML (ad.custom_html_safe).
export function AffiliateCard({ ad, placement, category = "", className = "", bypassOnePerPage = false }) {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!ad?.id) return;
    // reset page winner when navigation changes the URL
    if (typeof window !== "undefined" && window.location.href !== _pageLoadKey) {
      _pageLoadKey = window.location.href; _pageWinner = null;
    }
    const key = `${ad.id}:${placement}`;
    if (_fired.has(key)) return;
    getSettings().then((s) => {
      // Global one-affiliate-per-page-view: only the first affiliate on the page renders.
      // Fallback placements (e.g. hero_latest) opt out so they can always run.
      if (s.one_per_page && !bypassOnePerPage) {
        if (_pageWinner && _pageWinner !== ad.id) { setHidden(true); return; }
        _pageWinner = ad.id;
      }
      const seen = seenMap()[ad.id] || 0;
      if (s.frequency_cap > 0 && seen >= s.frequency_cap) { setHidden(true); return; }
      if (_fired.has(key)) return;
      _fired.add(key);
      bumpSeen(ad.id);
      api.post(`/affiliate/${ad.id}/impression`, { placement, category, page_url: window.location.pathname }).catch(() => {});
    });
  }, [ad?.id]); // eslint-disable-line

  if (!ad || hidden) return null;

  const onClick = () => api.post(`/affiliate/${ad.id}/click`, { placement, category, page_url: window.location.pathname }).catch(() => {});

  // Native card creative: whole card (and CTA) clickable, opens in a new tab.
  if (ad.creative_type === "native" || (ad.headline && !ad.custom_html_safe && !ad.image_only)) {
    return (
      <a href={ad.href} target={ad.target || "_blank"} rel="noopener noreferrer sponsored" onClick={onClick}
        data-testid={`affiliate-native-${placement}`}
        className={`block border border-gray-200 bg-white hover:shadow-md transition-shadow group ${className}`}>
        <div className="flex items-center justify-between px-3 pt-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-pti-red inline-block" aria-hidden="true" /> {ad.label || "Advertisement"}
          </span>
          {ad.category && <span className="text-[10px] text-gray-400">{ad.category}</span>}
        </div>
        {ad.image && (
          <div className="aspect-video overflow-hidden bg-gray-100 mt-2">
            <img src={ad.image} alt={ad.image_alt || ad.headline} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
        )}
        <div className="p-3">
          {(ad.logo || ad.partner) && (
            <div className="flex items-center gap-2 mb-1">
              {ad.logo && <img src={ad.logo} alt="" className="w-5 h-5 object-contain" />}
              {ad.partner && <span className="text-xs font-bold text-gray-700">{ad.partner}</span>}
            </div>
          )}
          <h4 className="font-display font-bold text-base leading-tight group-hover:text-pti-red transition-colors">{ad.headline}</h4>
          {ad.description && <p className="text-sm text-gray-600 mt-1 line-clamp-3">{ad.description}</p>}
          {ad.cta && (
            <span className="inline-block mt-3 bg-pti-red text-white text-xs font-bold uppercase tracking-widest px-4 py-2 group-hover:bg-pti-black transition-colors"
              data-testid={`affiliate-native-cta-${placement}`}>
              {ad.cta}
            </span>
          )}
        </div>
      </a>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-3 ${className}`} data-testid={`affiliate-${placement}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 flex items-center gap-1 mb-2">
        <span className="w-1.5 h-1.5 bg-pti-red inline-block" aria-hidden="true" /> {ad.label}
      </div>
      {ad.custom_html_safe ? (
        <div className="affiliate-html block [&_img]:max-w-full [&_img]:h-auto [&_img]:mx-auto [&_img]:block [&_img]:rounded"
          onClick={onClick} dangerouslySetInnerHTML={{ __html: ad.custom_html_safe }} />
      ) : (
        <a href={ad.href} target={ad.target || "_blank"} rel="noopener noreferrer sponsored" onClick={onClick} className="block">
          <img src={ad.image} alt={ad.image_alt || ad.name} loading="lazy"
            style={{ maxWidth: "100%", height: "auto" }} className="block mx-auto rounded" />
        </a>
      )}
    </div>
  );
}
